import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { organizationsApi, entitiesApi } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Search, ExternalLink, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ORG_TYPES = ["NGO","Foundation","Government","Chamber","Company","Community","Education","Developer","Professional","Other"];
const REL_STATUSES = ["Researching","Potential Fit","Outreach","Partner","Not Pursuing"];
const ORG_STATUSES = ["Active", "Inactive", "Unknown"];
const SCOPES = ["Local", "National", "Regional", "Global"];

const STATUS_COLOR = {
  "Researching": "bg-blue-500/15 text-blue-300 border-blue-500/25",
  "Potential Fit": "bg-amber-500/15 text-amber-300 border-amber-500/25",
  "Outreach": "bg-violet-500/15 text-violet-300 border-violet-500/25",
  "Partner": "bg-emerald-500/25 text-emerald-200 border-emerald-500/40",
  "Not Pursuing": "bg-white/5 text-white/50 border-white/15",
};

const ORG_STATUS_COLOR = {
  "Active": "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  "Inactive": "bg-white/5 text-white/50 border-white/15",
  "Unknown": "bg-white/5 text-white/40 border-white/10",
};

export default function OrganizationsPage() {
  const [rows, setRows] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [entityId, setEntityId] = useState("");
  const [orgType, setOrgType] = useState("");
  const [relStatus, setRelStatus] = useState("");
  const [orgStatus, setOrgStatus] = useState("");
  const [country, setCountry] = useState("");
  const [scope, setScope] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (entityId) params.entity_id = entityId;
      if (orgType) params.organization_type = orgType;
      if (relStatus) params.relationship_status = relStatus;
      if (orgStatus) params.organization_status = orgStatus;
      if (country) params.country = country;
      if (scope) params.geographic_scope = scope;
      const [r, e] = await Promise.all([
        organizationsApi.list(params),
        entities.length === 0 ? entitiesApi.list() : Promise.resolve({ data: entities }),
      ]);
      setRows(r.data || []);
      if (entities.length === 0) setEntities(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load organizations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId, orgType, relStatus, orgStatus, scope]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, country]);

  const entityName = (id) => entities.find(e => e.id === id)?.name || "";

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="organizations-page">
      <div className="max-w-6xl mx-auto p-6 md:p-10 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-white/70" />
              <h1 className="text-3xl font-display font-semibold tracking-tight">Organizations</h1>
            </div>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              Canonical directory of NGOs, foundations, companies, chambers, and community groups relevant to Alias entities. Discovered through Intake or captured manually.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, website, summary…"
                     className="pl-9 bg-white/5 border-white/10" data-testid="organizations-search" />
            </div>
            <Select value={entityId || "__all"} onValueChange={v => setEntityId(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-[180px] bg-white/5 border-white/10" data-testid="orgs-filter-entity">
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__all">All entities</SelectItem>
                {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={orgType || "__all"} onValueChange={v => setOrgType(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-[150px] bg-white/5 border-white/10" data-testid="orgs-filter-type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__all">All types</SelectItem>
                {ORG_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={relStatus || "__all"} onValueChange={v => setRelStatus(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-[170px] bg-white/5 border-white/10" data-testid="orgs-filter-rel-status">
                <SelectValue placeholder="All rel. statuses" />
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__all">All rel. statuses</SelectItem>
                {REL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={orgStatus || "__all"} onValueChange={v => setOrgStatus(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-[150px] bg-white/5 border-white/10" data-testid="orgs-filter-org-status">
                <SelectValue placeholder="All org statuses" />
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__all">Any org status</SelectItem>
                {ORG_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={country} onChange={e => setCountry(e.target.value)} placeholder="Country…"
                   className="bg-white/5 border-white/10 w-[130px]" data-testid="orgs-filter-country" />
            <Select value={scope || "__all"} onValueChange={v => setScope(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-[150px] bg-white/5 border-white/10" data-testid="orgs-filter-scope">
                <SelectValue placeholder="Any scope" />
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__all">Any scope</SelectItem>
                {SCOPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.01] p-10 text-center"
               data-testid="organizations-empty">
            <Building2 className="w-6 h-6 mx-auto text-white/30 mb-2" />
            <p className="text-sm text-white/50">No organizations match those filters yet.</p>
            <p className="text-xs text-white/30 mt-1">New organizations appear here as Intake surfaces them.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map(o => (
              <Link to={`/organizations/${o.id}`} key={o.id}
                    className="p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors block"
                    data-testid={`org-card-${o.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-lg font-medium text-white truncate">{o.name}</div>
                    <div className="flex items-center gap-2 text-xs text-white/50 mt-1 flex-wrap">
                      <Badge variant="outline" className="border-white/15 text-white/70 bg-white/5">{o.organization_type}</Badge>
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${ORG_STATUS_COLOR[o.organization_status || "Unknown"]}`}>
                        {o.organization_status || "Unknown"}
                      </span>
                      {o.website && (
                        <a href={o.website} target="_blank" rel="noopener noreferrer"
                           onClick={e => e.stopPropagation()}
                           className="inline-flex items-center gap-1 text-white/50 hover:text-white/85 truncate">
                          {o.domain || o.website} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-white/40 mt-1.5 flex-wrap">
                      {o.home_country && <span>{o.home_country}</span>}
                      {o.city_region && <span>· {o.city_region}</span>}
                      {o.geographic_scope && o.geographic_scope !== "Unknown" && (
                        <span>· {o.geographic_scope}</span>
                      )}
                    </div>
                  </div>
                </div>
                {o.summary && <p className="text-xs text-white/60 mt-3 line-clamp-2">{o.summary}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(o.relationships || []).slice(0, 4).map(r => (
                    <span key={r.id}
                          className={`text-[11px] px-2 py-0.5 rounded-md border ${STATUS_COLOR[r.relationship_status] || "bg-white/5 text-white/60 border-white/15"}`}>
                      {entityName(r.entity_id) || r.entity_id} · {r.relationship_status}
                    </span>
                  ))}
                  {(o.relationships || []).length > 4 && (
                    <span className="text-[11px] text-white/40">+{o.relationships.length - 4} more</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
